import { Any, MethodKind, } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import { errorFromJson } from '@connectrpc/connect/protocol-connect';
import { isTransportError, isTransportEvent, isTransportMessage, isTransportStream, } from './messages.js';
import ReadableStream from './ReadableStream.from.js';
const forceTransportOptions = {
    httpClient: null,
    baseUrl: 'https://in-memory',
    useBinaryFormat: false,
    acceptCompression: [],
    sendCompression: null,
    compressMinBytes: Number.MAX_SAFE_INTEGER,
    readMaxBytes: Number.MAX_SAFE_INTEGER,
    writeMaxBytes: Number.MAX_SAFE_INTEGER,
    interceptors: [],
};
/**
 * For use with `ConnectError.from`, in `rejectOnSignal`. Identifies an
 * appropriate error code for an unknown throw.
 * - ConnectError.from forwards exising ConnectError codes, ignoring this
 * - ConnectError.from uses `Code.Canceled` for an 'AbortError', ignoring this
 * - We want to apply `Code.DeadlineExceeded` for any 'TimeoutError'
 * - All others should use `Code.Aborted`
 */
const codeForError = (r) => {
    if (r instanceof DOMException && r.name === 'TimeoutError') {
        return Code.DeadlineExceeded;
    }
    else {
        return Code.Aborted;
    }
};
const rejectOnSignal = (...signals) => {
    return new Promise((_, reject) => {
        const signal = AbortSignal.any(signals.filter(s => s instanceof AbortSignal));
        signal.addEventListener('abort', () => reject(ConnectError.from(signal.reason, codeForError(signal.reason))));
        if (signal.aborted) {
            reject(ConnectError.from(signal.reason, codeForError(signal.reason)));
        }
    });
};
export const createChannelTransport = ({ getPort, jsonOptions, defaultTimeoutMs = 60_000, }) => {
    const pending = new Map();
    // this is used to recover errors that couldn't be thrown at a caller
    const transportFailure = new AbortController();
    // port returned by the penumbra global
    let port;
    /**
     * This function is called on the first request.  It begins channel init at
     * that moment, using the `getPort` function from options.  Message listeners
     * are attached during this process.  Failure will reject the first request.
     *
     * @returns A promise that resolves when the channel is acquired.
     */
    const connect = async () => {
        const connectionPort = await Promise.race([
            getPort(),
            rejectOnSignal(defaultTimeoutMs > 0 ? AbortSignal.timeout(defaultTimeoutMs) : undefined).catch(() => Promise.reject(new ConnectError('Channel connection request timed out', Code.Unavailable))),
        ]);
        connectionPort.addEventListener('message', transportListener);
        connectionPort.addEventListener('messageerror', (ev) => transportFailure.abort(ConnectError.from(ev.data)));
        connectionPort.start();
        return connectionPort;
    };
    const transportListener = ({ data }) => {
        if (data === false) {
            // 'false' indicating a disconnect
            transportFailure.abort(new ConnectError('Connection closed', Code.Unavailable));
        }
        else if (isTransportEvent(data)) {
            // this is a response to a specific request.  the port may be shared, so
            // it's okay if it contains a requestId we don't know about.  the response
            // may be successful, or contain an error conveyed only to the caller.
            pending.get(data.requestId)?.(data);
        }
        else if (isTransportError(data)) {
            // this is a channel-level error, corresponding to no specific request.
            // it will fail this transport, and every client using this transport, and
            // every transport using this channel. every transport sharing this port
            // will fail independently, but the rejection created here will be
            // delivered to every subsequent request attempted on this transport.
            transportFailure.abort(errorFromJson(data.error, data.metadata, new ConnectError('Transport failed')));
        }
        else {
            transportFailure.abort(new ConnectError('Unknown item in transport', Code.Unimplemented, undefined, undefined, data));
        }
    };
    return {
        async unary(service, method, signal, timeoutMs = defaultTimeoutMs, header, input) {
            transportFailure.signal.throwIfAborted();
            port ??= await connect();
            const requestId = crypto.randomUUID();
            const requestFailure = new AbortController();
            const response = Promise.race([
                rejectOnSignal(transportFailure.signal, requestFailure.signal, timeoutMs > 0 ? AbortSignal.timeout(timeoutMs) : undefined, signal),
                new Promise((resolve, reject) => {
                    pending.set(requestId, (tev) => {
                        if (isTransportMessage(tev, requestId)) {
                            resolve(tev);
                        }
                        else if (isTransportError(tev, requestId)) {
                            reject(errorFromJson(tev.error, tev.metadata, new ConnectError('Unary failed')));
                        }
                        else {
                            reject(ConnectError.from(tev));
                        }
                    });
                }),
            ]).finally(() => pending.delete(requestId));
            if (!signal?.aborted) {
                try {
                    switch (method.kind) {
                        case MethodKind.Unary:
                            {
                                const message = Any.pack(new method.I(input)).toJson(jsonOptions);
                                signal?.addEventListener('abort', () => port?.postMessage({ requestId, abort: true }));
                                port.postMessage({ requestId, message, header });
                            }
                            break;
                        default:
                            throw new ConnectError('MethodKind not supported', Code.Unimplemented);
                    }
                }
                catch (e) {
                    requestFailure.abort(e);
                }
            }
            return {
                service,
                method,
                stream: false,
                header: new Headers((await response).header),
                trailer: new Headers((await response).trailer),
                message: await response.then(({ message }) => {
                    const o = new method.O();
                    Any.fromJson(message, jsonOptions).unpackTo(o);
                    return o;
                }),
            };
        },
        async stream(service, method, signal, timeoutMs = defaultTimeoutMs, header, input) {
            transportFailure.signal.throwIfAborted();
            port ??= await connect();
            const requestId = crypto.randomUUID();
            const requestFailure = new AbortController();
            const response = Promise.race([
                rejectOnSignal(transportFailure.signal, requestFailure.signal, timeoutMs > 0 ? AbortSignal.timeout(timeoutMs) : undefined, signal),
                new Promise((resolve, reject) => {
                    pending.set(requestId, (tev) => {
                        if (isTransportStream(tev, requestId)) {
                            resolve(tev);
                        }
                        else if (isTransportError(tev, requestId)) {
                            reject(errorFromJson(tev.error, tev.metadata, new ConnectError('Stream failed')));
                        }
                        else {
                            reject(ConnectError.from(tev));
                        }
                    });
                }),
            ]).finally(() => pending.delete(requestId));
            if (!signal?.aborted) {
                try {
                    switch (method.kind) {
                        case MethodKind.ServerStreaming:
                            // send as a single message
                            {
                                // consume the input stream, which should have only one message
                                const iter = input[Symbol.asyncIterator]();
                                const [{ value } = { value: null }, { done }] = [
                                    await iter.next(),
                                    await iter.next(),
                                ];
                                // confirm the input stream ended after one message with content
                                if (done && typeof value === 'object' && value !== null) {
                                    const message = Any.pack(new method.I(value)).toJson(jsonOptions);
                                    port.postMessage({ requestId, message, header });
                                }
                                else {
                                    throw new ConnectError('MethodKind.ServerStreaming expects a single request message', Code.OutOfRange);
                                }
                            }
                            break;
                        case MethodKind.ClientStreaming:
                        case MethodKind.BiDiStreaming:
                            // send as an actual stream
                            {
                                const stream = ReadableStream.from(input).pipeThrough(new TransformStream({
                                    transform: (chunk, cont) => cont.enqueue(Any.pack(new method.I(chunk)).toJson(jsonOptions)),
                                }));
                                port.postMessage({ requestId, stream, header }, [stream]);
                            }
                            break;
                        default:
                            throw new ConnectError('MethodKind not supported', Code.Unimplemented);
                    }
                }
                catch (e) {
                    requestFailure.abort(e);
                }
            }
            return {
                service,
                method,
                stream: true,
                header: new Headers((await response).header),
                trailer: new Headers((await response).trailer),
                message: await response.then(({ stream }) => stream.pipeThrough(new TransformStream({
                    transform: (chunk, cont) => {
                        const o = new method.O();
                        Any.fromJson(chunk, jsonOptions).unpackTo(o);
                        cont.enqueue(o);
                    },
                }), { signal })),
            };
        },
    };
};
